import React, { useCallback, useEffect, useState } from 'react';
import { authFetch } from '../../../utils/cloudFunctionsAuth';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

interface ClientHostingTabProps {
  customerId: string;
  customerName: string;
}

interface Form {
  host: string;
  port: string;
  username: string;
  privateKey: string;
  passphrase: string;
  notes: string;
}

const EMPTY: Form = { host: '', port: '', username: '', privateKey: '', passphrase: '', notes: '' };

/**
 * Hosting / SSH access for a contact. Credentials live in the server-only,
 * encrypted `customer_secrets` collection, reached ONLY via owner-gated Cloud
 * Functions — never streamed to the page with the rest of the contact. Hidden by
 * default once stored: the owner sees a "configured" summary and must explicitly
 * Reveal to view/edit. The structured connection (host/user/key) is what the
 * approved, proxied agent SSH path uses; the key never leaves the server.
 */
export function ClientHostingTab({ customerId, customerName }: ClientHostingTabProps) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<{ connConfigured: boolean; host: string; port: number | null; username: string; hasKey: boolean; updatedAt: number | null }>({ connConfigured: false, host: '', port: null, username: '', hasKey: false, updatedAt: null });
  const [form, setForm] = useState<Form>(EMPTY);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<'reveal' | 'save' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const call = useCallback(async (fn: string, body: Record<string, unknown>) => {
    const r = await authFetch(`${CF_BASE}/${fn}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, ...body }),
    });
    const text = await r.text();
    let data: any = {}; try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
    if (!r.ok || data?.ok === false) throw new Error(data?.error || `HTTP ${r.status}`);
    return data;
  }, [customerId]);

  const loadStatus = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await call('getContactSecretStatus', {});
      setStatus({ connConfigured: !!d.connConfigured, host: d.host || '', port: d.port ?? null, username: d.username || '', hasKey: !!d.hasPrivateKey, updatedAt: d.updatedAt || null });
    } catch (e: any) {
      setError(e?.message || 'Could not load hosting status');
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    setForm(EMPTY); setEditing(false); setNotice(null);
    void loadStatus();
  }, [customerId, loadStatus]);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reveal = useCallback(async () => {
    setBusy('reveal'); setError(null);
    try {
      const d = await call('revealContactSecret', {});
      setForm({ host: d.host || '', port: d.port != null ? String(d.port) : '', username: d.username || '', privateKey: d.privateKey || '', passphrase: d.passphrase || '', notes: d.sshAccess || '' });
      setEditing(true);
    } catch (e: any) {
      setError(e?.message || 'Reveal failed');
    } finally {
      setBusy(null);
    }
  }, [call]);

  const save = useCallback(async () => {
    setBusy('save'); setError(null); setNotice(null);
    try {
      await call('saveContactSecret', {
        host: form.host, port: form.port, username: form.username,
        privateKey: form.privateKey, passphrase: form.passphrase, sshAccess: form.notes,
      });
      setEditing(false); setForm(EMPTY);
      setNotice('Saved & encrypted.');
      await loadStatus();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setBusy(null);
    }
  }, [call, form, loadStatus]);

  const clear = useCallback(async () => {
    if (!window.confirm(`Remove all stored SSH/hosting access for ${customerName}? This cannot be undone.`)) return;
    setBusy('clear'); setError(null); setNotice(null);
    try {
      await call('deleteContactSecret', {});
      setForm(EMPTY); setEditing(false);
      setNotice('Removed.');
      await loadStatus();
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
    } finally {
      setBusy(null);
    }
  }, [call, customerName, loadStatus]);

  const fmtWhen = (ms: number | null) => {
    if (!ms) return '';
    try { return new Date(ms).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h3 className="card-title fw-bold mb-0"><i className="bi bi-hdd-network me-2" />Hosting / SSH access</h3>
        {!loading && (
          <span className={`badge ${status.connConfigured ? 'badge-light-success' : 'badge-light-secondary'}`}>
            {status.connConfigured ? 'Configured' : 'Not set'}
          </span>
        )}
      </div>
      <div className="card-body py-4">
        <p className="text-muted fs-7 mb-4">
          SSH access for this customer's SiteGround site. Stored <strong>encrypted, server-side only</strong> — never loaded
          into the page with the rest of the contact, and hidden here until you reveal it. An authorised agent can request
          to run commands on this server; you approve each one and the key never leaves the server.
        </p>

        {error && <div className="alert alert-danger py-2">{error}</div>}
        {notice && <div className="alert alert-success py-2">{notice}</div>}

        {loading ? (
          <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" />Loading…</div>
        ) : editing ? (
          <>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fs-8 text-muted">Host</label>
                <input className="form-control form-control-sm" value={form.host} onChange={set('host')} placeholder="ssh.example.com" spellCheck={false} />
              </div>
              <div className="col-md-3">
                <label className="form-label fs-8 text-muted">Port</label>
                <input className="form-control form-control-sm" value={form.port} onChange={set('port')} placeholder="18765" inputMode="numeric" />
              </div>
              <div className="col-md-3">
                <label className="form-label fs-8 text-muted">Username</label>
                <input className="form-control form-control-sm" value={form.username} onChange={set('username')} placeholder="u123-site" spellCheck={false} />
              </div>
              <div className="col-12">
                <label className="form-label fs-8 text-muted">Private key (PEM/OpenSSH)</label>
                <textarea className="form-control form-control-sm font-monospace" rows={6} value={form.privateKey} onChange={set('privateKey')} placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----'} spellCheck={false} />
                <div className="form-text fs-8">Use a dedicated key for the agent — install its public key on the SiteGround account; we hold the private key encrypted and revocable.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label fs-8 text-muted">Key passphrase (optional)</label>
                <input type="password" className="form-control form-control-sm" value={form.passphrase} onChange={set('passphrase')} autoComplete="new-password" />
              </div>
              <div className="col-12">
                <label className="form-label fs-8 text-muted">Notes (optional)</label>
                <textarea className="form-control form-control-sm" rows={3} value={form.notes} onChange={set('notes')} placeholder="doc root, control-panel URL, anything else useful" />
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy === 'save'}>
                {busy === 'save' ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-lock me-2" />}Save (encrypted)
              </button>
              <button type="button" className="btn btn-light btn-sm" onClick={() => { setEditing(false); setForm(EMPTY); }} disabled={busy !== null}>Cancel</button>
            </div>
          </>
        ) : (
          <>
            {status.connConfigured ? (
              <div className="mb-3">
                <div className="d-flex flex-wrap gap-3 fs-7">
                  <span><span className="text-muted">Host:</span> <span className="fw-semibold">{status.host}{status.port ? `:${status.port}` : ''}</span></span>
                  <span><span className="text-muted">User:</span> <span className="fw-semibold">{status.username}</span></span>
                  <span><span className="text-muted">Key:</span> <span className="fw-semibold">{status.hasKey ? 'stored ••••' : '—'}</span></span>
                </div>
                {status.updatedAt && <div className="text-muted fs-8 mt-1">Last updated {fmtWhen(status.updatedAt)}.</div>}
              </div>
            ) : (
              <div className="text-muted fs-7 mb-3">No SSH access stored for this customer yet.</div>
            )}
            <div className="d-flex flex-wrap gap-2">
              <button type="button" className="btn btn-light-primary btn-sm" onClick={status.connConfigured ? reveal : () => setEditing(true)} disabled={busy === 'reveal'}>
                {busy === 'reveal' ? <span className="spinner-border spinner-border-sm me-2" /> : <i className={`bi ${status.connConfigured ? 'bi-eye' : 'bi-plus-lg'} me-2`} />}
                {status.connConfigured ? 'Reveal & edit' : 'Add SSH access'}
              </button>
              {status.connConfigured && (
                <button type="button" className="btn btn-light-danger btn-sm" onClick={clear} disabled={busy === 'clear'}>
                  <i className="bi bi-trash me-2" />Remove
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClientHostingTab;
